import { type MenuInputOption } from '@saviynt/design-system';

function distinctSortedCodes(codes: string[]): string[] {
  const nonEmpty = codes.filter((c) => c !== '');
  const seen = new Set<string>();

  const deduped = nonEmpty.filter((c) => {
    if (seen.has(c)) {
      return false;
    }

    seen.add(c);

    return true;
  });

  return deduped.sort((a, b) => a.localeCompare(b));
}

function codesFromSingleFormValue(raw: unknown): string[] {
  if (raw === null || raw === undefined || raw === '') {
    return [];
  }

  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    const v = (raw as MenuInputOption).value;

    if (v === '' || v == null) {
      return [];
    }

    return [String(v)];
  }

  if (typeof raw === 'string' || typeof raw === 'number') {
    const s = String(raw).trim();

    return s ? [s] : [];
  }

  return [];
}

function codesFromMultiFormValue(raw: unknown): string[] {
  if (raw === null || raw === undefined) {
    return [];
  }

  if (Array.isArray(raw)) {
    const codes = raw.flatMap((item): string[] => {
      if (typeof item === 'string') {
        return [item];
      }

      if (typeof item === 'object' && item !== null && 'value' in item) {
        const v = (item as MenuInputOption).value;

        if (v != null && v !== '') {
          return [String(v)];
        }
      }

      return [];
    });

    return distinctSortedCodes(codes);
  }

  if (typeof raw === 'string') {
    return distinctSortedCodes(
      raw
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    );
  }

  if (typeof raw === 'number') {
    return [String(raw)];
  }

  return [];
}

function codesFromLoadedSingle(loaded: unknown): string[] {
  if (loaded === null || loaded === undefined || loaded === '') {
    return [];
  }

  if (Array.isArray(loaded)) {
    const codes = loaded
      .map((item) => codesFromSingleFormValue(item))
      .find((c) => c.length > 0);

    return codes ?? [];
  }

  return codesFromSingleFormValue(loaded);
}

function codesFromLoadedMulti(loaded: unknown): string[] {
  if (loaded === null || loaded === undefined) {
    return [];
  }

  if (Array.isArray(loaded)) {
    const codes = loaded.flatMap((item): string[] => {
      if (typeof item === 'string') {
        return [item];
      }

      if (typeof item === 'object' && item !== null && 'value' in item) {
        const v = (item as { value: unknown }).value;

        if (v != null && v !== '') {
          return [String(v)];
        }
      }

      return [];
    });

    return distinctSortedCodes(codes);
  }

  if (typeof loaded === 'string') {
    return distinctSortedCodes(
      loaded
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    );
  }

  if (typeof loaded === 'number') {
    return [String(loaded)];
  }

  if (typeof loaded === 'object' && loaded !== null && 'value' in loaded) {
    const v = (loaded as { value: unknown }).value;

    if (v !== '' && v != null) {
      return [String(v)];
    }
  }

  return [];
}

/**
 * True when the current Formik value represents the same option codes as the `/load` snapshot.
 */
export function loadedSnapshotMatchesCurrentSelection(
  loaded: unknown,
  current: unknown,
  fieldType: 'singleselect' | 'multiselect'
): boolean {
  if (fieldType === 'singleselect') {
    const a = codesFromLoadedSingle(loaded).join('\0');
    const b = codesFromSingleFormValue(current).join('\0');

    return a === b && a !== '';
  }

  const a = codesFromLoadedMulti(loaded).join('\0');
  const b = codesFromMultiFormValue(current).join('\0');

  return a === b && a !== '';
}

/**
 * Builds `valuesToSearch` for data-provider POST bodies from the raw `/load` field payload.
 * Wraps non-array snapshots in a one-element array so JSON bodies stay consistent.
 */
export function normalizeValuesToSearchWireFromLoaded(
  loaded: unknown
): unknown[] | undefined {
  if (loaded === null || loaded === undefined) {
    return undefined;
  }

  if (Array.isArray(loaded)) {
    return loaded.length > 0 ? Array.from(loaded) : undefined;
  }

  return [loaded];
}

/**
 * Whether `valuesToSearch` should be attached to a provider payload (non-empty after normalization).
 */
export function shouldAttachValuesToSearch(wire: unknown): wire is unknown[] {
  return Array.isArray(wire) && wire.length > 0;
}
